import {
  BiBarGap,
  BiCornerRadius,
  BiVizStyle,
  BiVizType,
} from '@euler/model/bi/viz';
import {
  BiGalleryCategory,
  BiGalleryCategoryKey,
  BiGalleryDashboardCard,
  BiGalleryVariant,
} from '../functions/biGalleryTypes';
import {
  cents,
  kGalleryFields as f,
  galleryReady,
  galleryResult,
  kGalleryLongItems,
} from './biGalleryFields';

const types = ['常规保养', '钣金喷漆', '轮胎更换', '美容洗护', '电瓶更换'];
const parts = [86400, 51200, 38700, 12600, 9800];
const labor = [42100, 36800, 8900, 21400, 2600];
const itemOrders = [148, 96, 83, 77, 64, 41];

const groupedResult = galleryResult(
  [f.serviceType, f.partsRevenue, f.laborRevenue],
  types.map((type, i) => [type, cents(parts[i]), cents(labor[i])]),
);
const longResult = galleryResult(
  [f.item, f.orders],
  kGalleryLongItems.map((item, i) => [item, itemOrders[i]]),
);

function groupedCard(style?: BiVizStyle): BiGalleryDashboardCard {
  return {
    surface: 'dashboard',
    title: '本月各服务类型营收构成',
    viz: {
      type: BiVizType.Bar,
      x: f.serviceType.key,
      series: [{ key: f.partsRevenue.key }, { key: f.laborRevenue.key }],
      ...(style ? { style } : {}),
    },
    data: galleryReady(groupedResult),
  };
}

function radiusVariant(cornerRadius: BiCornerRadius): BiGalleryVariant {
  return {
    key: `radius-${cornerRadius}`,
    name: `圆角 ${cornerRadius}`,
    note: `bar.cornerRadius = ${cornerRadius}`,
    card: groupedCard({ bar: { cornerRadius, gap: BiBarGap.Normal } }),
  };
}

const kGapNames: Record<BiBarGap, string> = {
  [BiBarGap.Narrow]: '窄',
  [BiBarGap.Normal]: '常规',
  [BiBarGap.Wide]: '宽',
};

function gapVariant(gap: BiBarGap): BiGalleryVariant {
  return {
    key: `gap-${gap}`,
    name: `间距 ${kGapNames[gap]}`,
    note: `bar.gap = ${gap}`,
    card: groupedCard({ bar: { cornerRadius: 4, gap } }),
  };
}

function longCard(style?: BiVizStyle): BiGalleryDashboardCard {
  return {
    surface: 'dashboard',
    title: '热门服务项目工单数',
    viz: {
      type: BiVizType.Bar,
      x: f.item.key,
      series: [{ key: f.orders.key }],
      ...(style ? { style } : {}),
    },
    data: galleryReady(longResult),
  };
}

const grouped = groupedCard();

export const kBarCategory: BiGalleryCategory = {
  key: BiGalleryCategoryKey.Bar,
  name: '柱状图',
  description: '分组柱、数据标签、圆角、间距、长中文标签',
  preview: grouped,
  variants: [
    {
      key: 'grouped',
      name: '分组柱状图',
      note: '两个指标并排',
      card: grouped,
    },
    {
      key: 'labels',
      name: '数据标签',
      note: 'dataLabels = true',
      card: groupedCard({ dataLabels: true }),
    },
    radiusVariant(0),
    radiusVariant(4),
    radiusVariant(8),
    gapVariant(BiBarGap.Narrow),
    gapVariant(BiBarGap.Normal),
    gapVariant(BiBarGap.Wide),
    {
      key: 'long-labels',
      name: '长中文标签 · 自动',
      note: '放不下时转 45°，截断处加省略号，悬停或点按看全称',
      card: longCard(),
    },
    {
      key: 'long-labels-0',
      name: '长中文标签 · 不旋转',
      note: 'axes.x.labelRotate = 0',
      card: longCard({ axes: { x: { show: true, labelRotate: 0 } } }),
    },
    {
      key: 'long-labels-45',
      name: '长中文标签 · 旋转 45°',
      note: 'axes.x.labelRotate = 45',
      card: longCard({ axes: { x: { show: true, labelRotate: 45 } } }),
    },
    {
      key: 'long-labels-90',
      name: '长中文标签 · 旋转 90°',
      note: 'axes.x.labelRotate = 90',
      card: {
        ...longCard({ axes: { x: { show: true, labelRotate: 90 } } }),
        rows: 6,
      },
    },
  ],
};
